import { Box, HStack, IconButton, List, ListItem, Progress, Text } from '@chakra-ui/react';
import { FileText, X } from 'lucide-react';
import { useContext } from 'react';

import { FileUploadContext } from './FileUploadContext';
import { FileUpload } from '@duke-hq/svc-files-lib/schemas';

/**
 * FileUploadList component
 *
 * This component displays a list of files being uploaded. Each file in the list can be removed individually.
 * It must be used within a FileUploadContextProvider component.
 */
export function FileUploadList({
  filter = () => true,
  sort = (a, b) => {
    // default sort by added order
    if (a.id < b.id) {
      return -1;
    }

    return 0;
  },
  hideCompletedFiles = false,
}: {
  filter?: (file: FileUpload) => boolean;
  sort?: (a: FileUpload, b: FileUpload) => number;
  hideCompletedFiles?: boolean;
}) {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUploadList must be used within a FileUploadContextProvider');
  }
  const { files } = context;

  if (files.length === 0) {
    return null;
  }

  return (
    <List spacing={2} w="full">
      {files
        .filter((file) => {
          if (hideCompletedFiles && file.status === 'complete') {
            return false;
          }
          return filter(file);
        })
        .sort(sort)
        .map((file) => {
          let progress: number | undefined;
          if (file.status === 'pending') {
            progress = file.progress;
          } else if (file.status === 'confirmingUpload') {
            progress = 100;
          }

          return (
            <ListItem key={file.id} display="flex" alignItems="center">
              <HStack
                key={file.id}
                w="full"
                borderRadius="8px"
                borderColor="border.accent"
                borderWidth="1px"
                bgColor="bg.accent.canvas"
                padding="16px"
                gap="16px"
                justifyContent="space-between"
              >
                <HStack spacing="1" alignItems="center" minW={0}>
                  <Box flexShrink={0} width="30px">
                    <FileText size="30px" />
                  </Box>
                  <Text isTruncated maxW="100%" flexShrink={1}>
                    {file.name}
                  </Text>
                </HStack>

                <HStack alignItems="center" justifyContent="flex-end">
                  {file.failed ? (
                    <Text color="red.500">Failed</Text>
                  ) : typeof progress !== 'undefined' ? (
                    <Box
                      width={{
                        base: '100px',
                        sm: '150px',
                        md: '200px',
                        lg: '250px',
                      }}
                    >
                      <Progress
                        value={progress}
                        isAnimated={true}
                        hasStripe={true}
                        sx={{
                          '& > div': {
                            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                          },
                        }}
                      />
                    </Box>
                  ) : null}

                  <IconButton
                    aria-label="Remove"
                    icon={<X size="20px" />}
                    variant="ghost"
                    color="fg.default"
                    onClick={file.remove}
                  />
                </HStack>
              </HStack>
            </ListItem>
          );
        })}
    </List>
  );
}
